import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { toast } from 'sonner';
import api from '@/lib/axios';
import { getErrorMessage } from '@/lib/errors';
import { calcularOrden, rangoDeHoy } from '@/lib/tareas';
import { useAuthStore, ROL } from '@/store/auth.store';
import type {
  ActualizarTareaInput, CrearTareaInput, EtiquetaTarea,
  FiltrosTareas, PlantillaColumna, ResumenMias, Tablero, TableroCompleto, Tarea, TareaDetalle, TareasPaginadas,
  UsuarioAsignable,
} from '@/types/tareas';

export const tareasKeys = {
  todas: ['tareas'] as const,
  tableros: ['tareas', 'tableros'] as const,
  tablero: (id: string) => ['tareas', 'tablero', id] as const,
  lista: (f: FiltrosTareas) => ['tareas', 'lista', f] as const,
  resumen: ['tareas', 'mias-resumen'] as const,
  detalle: (id: string) => ['tareas', 'detalle', id] as const,
  etiquetas: ['tareas', 'etiquetas'] as const,
  asignables: ['tareas', 'asignables'] as const,
  plantilla: ['tareas', 'plantilla'] as const,
  checklists: (id: string) => ['tareas', 'checklists', id] as const,
  comentarios: (id: string) => ['tareas', 'comentarios', id] as const,
  adjuntos: (id: string) => ['tareas', 'adjuntos', id] as const,
  actividad: (id: string) => ['tareas', 'actividad', id] as const,
};

/** Roles que usan el módulo; recepcionista queda fuera por decisión de negocio. */
export const ROLES_TAREAS: string[] = [ROL.SUPER_ADMIN, ROL.ADMIN_CLINICA, ROL.MEDICO_ADMIN, ROL.MEDICO, ROL.ASISTENTE];
export const ROLES_TAREAS_ADMIN: string[] = [ROL.SUPER_ADMIN, ROL.ADMIN_CLINICA, ROL.MEDICO_ADMIN];

export function usePermisosTareas() {
  const rolId = useAuthStore(s => s.usuario?.rolId);
  return {
    puedeUsar: !!rolId && ROLES_TAREAS.includes(rolId),
    esAdmin: !!rolId && ROLES_TAREAS_ADMIN.includes(rolId),
  };
}

function limpiar(filtros: FiltrosTareas) {
  return Object.fromEntries(
    Object.entries(filtros).filter(([, v]) => v !== undefined && v !== false && v !== ''),
  );
}

export function useTableros(enabled = true) {
  return useQuery<{ tableros: Tablero[]; defaultId: string | null }>({
    queryKey: tareasKeys.tableros,
    queryFn: () => api.get('/api/tareas/tableros').then(r => r.data),
    enabled,
    staleTime: 60_000,
  });
}

export function useTablero(id: string | undefined) {
  return useQuery<TableroCompleto>({
    queryKey: tareasKeys.tablero(id ?? ''),
    queryFn: () => api.get(`/api/tareas/tableros/${id}`).then(r => r.data),
    enabled: !!id,
    placeholderData: keepPreviousData,
  });
}

export function useTareasLista(filtros: FiltrosTareas, enabled = true) {
  return useQuery<TareasPaginadas>({
    queryKey: tareasKeys.lista(filtros),
    queryFn: () => api.get('/api/tareas', { params: limpiar(filtros) }).then(r => r.data),
    enabled,
    placeholderData: keepPreviousData,
  });
}

export function useResumenMias(enabled = true) {
  const rango = useMemo(() => rangoDeHoy(), []);
  return useQuery<ResumenMias>({
    queryKey: tareasKeys.resumen,
    queryFn: () => api.get('/api/tareas/mias/resumen', {
      params: {
        hoyInicio: rango.hoyInicio.toISOString(),
        hoyFin: rango.hoyFin.toISOString(),
        semanaFin: rango.semanaFin.toISOString(),
      },
    }).then(r => r.data),
    enabled,
    staleTime: 30_000,
  });
}

export function useTarea(id: string | null | undefined) {
  return useQuery<TareaDetalle>({
    queryKey: tareasKeys.detalle(id ?? ''),
    queryFn: () => api.get(`/api/tareas/${id}`).then(r => r.data),
    enabled: !!id,
  });
}

export function useEtiquetas(enabled = true) {
  return useQuery<EtiquetaTarea[]>({
    queryKey: tareasKeys.etiquetas,
    queryFn: () => api.get('/api/tareas/etiquetas').then(r => r.data),
    enabled,
    staleTime: 5 * 60_000,
  });
}

export function useAsignables(enabled = true) {
  return useQuery<UsuarioAsignable[]>({
    queryKey: tareasKeys.asignables,
    queryFn: () => api.get('/api/tareas/usuarios-asignables').then(r => r.data),
    enabled,
    staleTime: 5 * 60_000,
  });
}

export function usePlantillaColumnas() {
  return useQuery<PlantillaColumna[]>({
    queryKey: tareasKeys.plantilla,
    queryFn: () => api.get('/api/tareas/plantilla-columnas').then(r => r.data),
  });
}

/* ── Mutaciones ─────────────────────────────────────────────────────────── */

/**
 * Actualiza en caché la tarjeta afectada y deja que las demás vistas se re-sincronicen sin parpadeo:
 * el tablero se parchea a mano y el resto se invalida.
 */
function useSincronizarCache() {
  const qc = useQueryClient();
  return (detalle: TareaDetalle) => {
    qc.setQueryData(tareasKeys.detalle(detalle.id), detalle);
    qc.setQueryData<TableroCompleto>(tareasKeys.tablero(detalle.tableroId), prev => {
      if (!prev) return prev;
      const { subtareasLista: _s, columna: _c, tablero: _t, descripcion: _d, ...tarjeta } = detalle;
      if (detalle.parentId) return prev;
      const existe = prev.tareas.some(t => t.id === detalle.id);
      return {
        ...prev,
        tareas: existe ? prev.tareas.map(t => (t.id === detalle.id ? tarjeta : t)) : [...prev.tareas, tarjeta],
      };
    });
    // Una subtarea cambia los contadores y la lista del detalle de su tarea padre.
    if (detalle.parentId) {
      qc.invalidateQueries({ queryKey: tareasKeys.detalle(detalle.parentId) });
      qc.invalidateQueries({ queryKey: tareasKeys.tablero(detalle.tableroId) });
    }
    qc.invalidateQueries({ queryKey: tareasKeys.actividad(detalle.id) });
    qc.invalidateQueries({ queryKey: ['tareas', 'lista'] });
    qc.invalidateQueries({ queryKey: tareasKeys.resumen });
  };
}

export function useCrearTarea() {
  const sincronizar = useSincronizarCache();
  return useMutation({
    mutationFn: (input: CrearTareaInput) => api.post<TareaDetalle>('/api/tareas', input).then(r => r.data),
    onSuccess: (detalle) => {
      sincronizar(detalle);
      toast.success('Tarea creada');
    },
    onError: (e) => toast.error(getErrorMessage(e, 'No se pudo crear la tarea')),
  });
}

export function useActualizarTarea() {
  const sincronizar = useSincronizarCache();
  return useMutation({
    mutationFn: ({ id, ...input }: ActualizarTareaInput & { id: string }) =>
      api.patch<TareaDetalle>(`/api/tareas/${id}`, input).then(r => r.data),
    onSuccess: sincronizar,
    onError: (e) => toast.error(getErrorMessage(e, 'No se pudo guardar la tarea')),
  });
}

interface MoverInput {
  id: string;
  columnaId: string;
  antesDeId?: string;
  despuesDeId?: string;
}

export function useMoverTarea(tableroId: string | undefined) {
  const qc = useQueryClient();
  const sincronizar = useSincronizarCache();
  return useMutation({
    mutationFn: ({ id, ...body }: MoverInput) =>
      api.patch<TareaDetalle>(`/api/tareas/${id}/mover`, body).then(r => r.data),
    onMutate: async ({ id, columnaId, antesDeId, despuesDeId }) => {
      // El modal de detalle lee de su propia consulta: se adelanta también ahí para que el cambio se vea al instante.
      const claveDetalle = tareasKeys.detalle(id);
      await qc.cancelQueries({ queryKey: claveDetalle });
      const detallePrevio = qc.getQueryData<TareaDetalle>(claveDetalle);
      const key = tableroId ? tareasKeys.tablero(tableroId) : undefined;
      if (key) await qc.cancelQueries({ queryKey: key });
      const previo = key ? qc.getQueryData<TableroCompleto>(key) : undefined;

      const destino = previo?.columnas.find(c => c.id === columnaId)
        ?? (detallePrevio?.columna?.id === columnaId ? detallePrevio.columna : undefined);

      if (detallePrevio && previo) {
        const col = previo.columnas.find(c => c.id === columnaId);
        if (col) {
          qc.setQueryData<TareaDetalle>(claveDetalle, {
            ...detallePrevio,
            columnaId,
            columnaNombre: col.nombre,
            columna: { id: col.id, nombre: col.nombre, esFinal: col.esFinal },
            completadaAt: col.esFinal ? (detallePrevio.completadaAt ?? new Date().toISOString()) : null,
          });
        }
      }

      const tarea = previo?.tareas.find(t => t.id === id);
      if (!key || !previo || !tarea || !destino) return { previo, detallePrevio };

      const hermanas = previo.tareas
        .filter(t => t.columnaId === columnaId && t.id !== id)
        .sort((a, b) => a.orden - b.orden);
      let indice = hermanas.length;
      if (antesDeId) {
        const i = hermanas.findIndex(t => t.id === antesDeId);
        if (i >= 0) indice = i;
      } else if (despuesDeId) {
        const i = hermanas.findIndex(t => t.id === despuesDeId);
        if (i >= 0) indice = i + 1;
      }
      const orden = calcularOrden(hermanas, indice);

      qc.setQueryData<TableroCompleto>(key, {
        ...previo,
        tareas: previo.tareas.map(t => t.id === id
          ? { ...t, columnaId, orden, completadaAt: ('esFinal' in destino && destino.esFinal) ? (t.completadaAt ?? new Date().toISOString()) : null }
          : t),
      });
      return { previo, detallePrevio };
    },
    onError: (e, vars, contexto) => {
      if (tableroId && contexto?.previo) qc.setQueryData(tareasKeys.tablero(tableroId), contexto.previo);
      if (contexto?.detallePrevio) qc.setQueryData(tareasKeys.detalle(vars.id), contexto.detallePrevio);
      toast.error(getErrorMessage(e, 'No se pudo mover la tarea'));
    },
    onSuccess: sincronizar,
  });
}

export function useEliminarTarea() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (tarea: Pick<Tarea, 'id' | 'tableroId'>) => api.delete(`/api/tareas/${tarea.id}`).then(() => tarea),
    onSuccess: (tarea) => {
      qc.setQueryData<TableroCompleto>(tareasKeys.tablero(tarea.tableroId), prev =>
        prev ? { ...prev, tareas: prev.tareas.filter(t => t.id !== tarea.id) } : prev);
      qc.removeQueries({ queryKey: tareasKeys.detalle(tarea.id) });
      qc.invalidateQueries({ queryKey: ['tareas', 'detalle'] });
      qc.invalidateQueries({ queryKey: ['tareas', 'lista'] });
      qc.invalidateQueries({ queryKey: tareasKeys.resumen });
      toast.success('Tarea eliminada');
    },
    onError: (e) => toast.error(getErrorMessage(e, 'No se pudo eliminar la tarea')),
  });
}

export function useAgregarRelacion() {
  const sincronizar = useSincronizarCache();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string; entidadTipo: string; entidadId: string }) =>
      api.post<TareaDetalle>(`/api/tareas/${id}/relaciones`, body).then(r => r.data),
    onSuccess: sincronizar,
    onError: (e) => toast.error(getErrorMessage(e, 'No se pudo agregar la relación')),
  });
}

export function useQuitarRelacion() {
  const sincronizar = useSincronizarCache();
  return useMutation({
    mutationFn: ({ id, relacionId }: { id: string; relacionId: string }) =>
      api.delete<TareaDetalle>(`/api/tareas/${id}/relaciones/${relacionId}`).then(r => r.data),
    onSuccess: sincronizar,
    onError: (e) => toast.error(getErrorMessage(e, 'No se pudo quitar la relación')),
  });
}

export function useCompletarTarea() {
  const sincronizar = useSincronizarCache();
  return useMutation({
    mutationFn: ({ id, completar }: { id: string; completar: boolean }) =>
      api.post<TareaDetalle>(`/api/tareas/${id}/${completar ? 'completar' : 'reabrir'}`).then(r => r.data),
    onSuccess: sincronizar,
    onError: (e) => toast.error(getErrorMessage(e, 'No se pudo actualizar la tarea')),
  });
}
